import { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useCountries } from "@/lib/countries";
import { Card } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import BrandingCard from "@/components/BrandingCard";
import { X, Plus, Sparkles, AlertTriangle } from "lucide-react";

export const COMPANY_TYPES = [
  { code: "customer_support",        label: "Customer Support / Contact Centre" },
  { code: "office_professional",     label: "Office / Professional Services" },
  { code: "retail",                  label: "Retail" },
  { code: "hospitality",             label: "Hospitality" },
  { code: "logistics_transport",     label: "Logistics & Transport" },
  { code: "manufacturing_warehouse", label: "Manufacturing & Warehouse" },
  { code: "healthcare",              label: "Healthcare" },
  { code: "education",               label: "Education" },
  { code: "field_services",          label: "Field Services" },
  { code: "other",                   label: "Other" },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const countries = useCountries();
  const [company, setCompany] = useState(null);
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const [defaults, setDefaults] = useState(null);
  const [ticked, setTicked] = useState({});
  const [typeChangeWarn, setTypeChangeWarn] = useState(false);
  const [busy, setBusy] = useState(false);
  // Display preferences (separate per-company doc).
  const [display, setDisplay] = useState(null);
  const [displayBusy, setDisplayBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/companies");
      const c = data.find(c => c.company_id === user.company_id) || data[0];
      setCompany({ ...c, countries: c.countries || [] });
      try {
        const { data: d } = await api.get("/me/display-settings");
        setDisplay(d);
      } catch { /* keep null if user lacks permission */ }
    })();
  }, [user.company_id]);

  const saveDisplay = async (patch) => {
    setDisplayBusy(true);
    try {
      const { data } = await api.put("/me/display-settings", patch);
      setDisplay(data);
      toast.success("Display preferences saved");
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail));
    } finally {
      setDisplayBusy(false);
    }
  };

  const typeLabel = useMemo(() => {
    const m = COMPANY_TYPES.find(t => t.code === company?.company_type);
    return m?.label || "-";
  }, [company?.company_type]);

  const addCountry = (code) => {
    if (!company.countries.includes(code))
      setCompany({ ...company, countries: [...company.countries, code] });
  };
  const removeCountry = (code) => setCompany({
    ...company, countries: company.countries.filter(c => c !== code) });

  const save = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const wantPromptDefaults = company.company_type &&
        company.company_type !== company.__originalType;
      const { data } = await api.patch(`/companies/${company.company_id}`, {
        name: company.name,
        timezone: company.timezone,
        countries: company.countries,
        company_type: company.company_type || null,
      });
      setCompany({ ...data, countries: data.countries || [] });
      toast.success("Company updated");
      if (wantPromptDefaults) {
        await openDefaults();
      }
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openDefaults = async () => {
    try {
      const { data } = await api.get(`/companies/${company.company_id}/default-assignments`);
      if (!data.suggestions?.length) return;
      setDefaults(data);
      setTicked(Object.fromEntries(
        data.suggestions.filter(s => !s.already_exists).map(s => [s.code, true])
      ));
      setDefaultsOpen(true);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const applyDefaults = async () => {
    const codes = Object.entries(ticked).filter(([, v]) => v).map(([c]) => c);
    if (!codes.length) { setDefaultsOpen(false); return; }
    try {
      const { data } = await api.post(`/companies/${company.company_id}/apply-default-assignments`, { codes });
      toast.success(`Added ${data.added} default assignment${data.added === 1 ? "" : "s"}`);
      setDefaultsOpen(false);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  if (!company) return null;
  const selectedCountryObjs = countries.filter(c => company.countries.includes(c.code));

  return (
    <div>
      <div className="eyebrow">Workspace</div>
      <h1 className="font-display text-4xl mt-1 mb-2">Company settings</h1>
      <p className="text-sm text-[#4B5773] mb-8">Configure where you operate and which industry you're in. These choices tailor dropdowns and starter content throughout NextRota.</p>

      <div className="max-w-2xl space-y-6">
        <form onSubmit={save} className="space-y-6">
          <Card title="General">
            <div className="space-y-4">
              <div>
                <Label className="eyebrow">Company name</Label>
                <Input value={company.name} data-testid="company-name-input"
                  onChange={(e)=>setCompany({...company, name:e.target.value})}
                  className="mt-2 h-11 rounded-lg"/>
              </div>
              <div>
                <Label className="eyebrow">Default timezone</Label>
                <Input value={company.timezone || ""} data-testid="company-tz-input"
                  onChange={(e)=>setCompany({...company, timezone:e.target.value})}
                  className="mt-2 h-11 rounded-lg"/>
              </div>
            </div>
          </Card>

          <Card title="Company countries"
                action={<span className="text-xs text-[#4B5773]">{company.countries.length} selected</span>}>
            <p className="text-xs text-[#4B5773] mb-3">Countries you operate in. These will be the only options offered in employee country dropdowns and bank-holiday country filters.</p>
            <div className="flex flex-wrap gap-1.5 mb-3 min-h-[2.25rem]" data-testid="country-chip-list">
              {selectedCountryObjs.length === 0 && (
                <span className="text-xs text-slate-400">No countries selected - add at least one below.</span>
              )}
              {selectedCountryObjs.map(c => (
                <span key={c.code} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#DBE7FE] text-[#1D4ED8] text-xs">
                  {c.name}
                  <button type="button" onClick={()=>removeCountry(c.code)}
                    data-testid={`country-remove-${c.code}`}
                    className="hover:text-red-600 -mr-1"><X className="h-3 w-3"/></button>
                </span>
              ))}
            </div>
            <Select value="" onValueChange={addCountry}>
              <SelectTrigger className="h-10 rounded-lg w-full sm:w-72" data-testid="add-country-select">
                <SelectValue placeholder="+ Add a country"/>
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {countries
                  .filter(c => !company.countries.includes(c.code))
                  .map(c => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Card>

          <Card title="Company type"
                action={company.company_type ? <span className="text-xs text-[#4B5773]">{typeLabel}</span> : null}>
            <p className="text-xs text-[#4B5773] mb-3">Pick the option closest to your industry. NextRota will suggest a starter set of assignments tailored to that work.</p>
            <Select value={company.company_type || ""}
              onValueChange={(v) => {
                if (company.company_type && company.company_type !== v) setTypeChangeWarn(true);
                setCompany(prev => ({ ...prev, company_type: v, __originalType: prev.company_type }));
              }}>
              <SelectTrigger className="h-10 rounded-lg w-full sm:w-80" data-testid="company-type-select">
                <SelectValue placeholder="Select a company type"/>
              </SelectTrigger>
              <SelectContent>
                {COMPANY_TYPES.map(t => (
                  <SelectItem key={t.code} value={t.code} data-testid={`type-opt-${t.code}`}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {typeChangeWarn && (
              <div className="mt-3 flex gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900"
                   data-testid="type-change-warning">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5"/>
                <div>
                  <div className="font-medium">Changing company type</div>
                  Existing assignments stay as they are. New default assignments won't replace anything - we'll offer them on save and you can pick which to add.
                </div>
              </div>
            )}
            {company.company_type && (
              <button type="button" onClick={openDefaults} data-testid="apply-defaults-link"
                className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#2563EB] hover:underline">
                <Sparkles className="h-3.5 w-3.5"/> Add suggested assignments for this type
              </button>
            )}
          </Card>

          <Button type="submit" disabled={busy} data-testid="company-save-btn"
            className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </form>

        {/* Display preferences */}
        {display && (
          <Card title="Display preferences">
            <p className="text-xs text-[#4B5773] mb-3">
              Choose how shifts and rosters appear to your team.
            </p>
            <label className="flex items-start gap-3 cursor-pointer"
                    data-testid="display-show-locations-row">
              <input type="checkbox"
                checked={!!display.show_locations_on_rota}
                disabled={displayBusy}
                onChange={(e) => saveDisplay({ show_locations_on_rota: e.target.checked })}
                className="mt-1 accent-[#2563EB]"
                data-testid="display-show-locations-toggle" />
              <span>
                <span className="font-medium text-sm text-[#0B1A36]">
                  Show location on rota
                </span>
                <span className="block text-xs text-[#4B5773] mt-0.5">
                  Display work locations on roster cards so managers and employees
                  can quickly see where each shift is scheduled to take place.
                </span>
              </span>
            </label>
          </Card>
        )}

        <BrandingCard />
      </div>

      {/* Defaults dialog */}
      <Dialog open={defaultsOpen} onOpenChange={setDefaultsOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add default assignments - {typeLabel}</DialogTitle></DialogHeader>
          <p className="text-sm text-[#4B5773]">Tick which suggested assignments you'd like to create. You can add or remove any of them later.</p>
          <div className="space-y-1 mt-2 max-h-80 overflow-y-auto" data-testid="defaults-list">
            {(defaults?.suggestions || []).map(s => (
              <label key={s.code}
                className={`flex items-center gap-3 px-3 py-2 rounded-md hover:bg-slate-50 cursor-pointer ${s.already_exists ? "opacity-50" : ""}`}>
                <input type="checkbox"
                  data-testid={`default-${s.code}`}
                  checked={!!ticked[s.code]} disabled={s.already_exists}
                  onChange={(e) => setTicked({ ...ticked, [s.code]: e.target.checked })}
                  className="accent-[#2563EB]"/>
                <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: s.color }}/>
                <span className="flex-1 text-sm">{s.name}</span>
                {s.already_exists && <span className="text-[10px] uppercase tracking-widest text-slate-400">added</span>}
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setDefaultsOpen(false)} className="rounded-lg">Cancel</Button>
            <Button onClick={applyDefaults} data-testid="defaults-apply-btn"
              className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
              <Plus className="h-4 w-4 mr-1"/>Add selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
